import { useCallback, useState } from 'react';
import {
  lookupRui,
  type RuiLookupApiInput,
  type RuiLookupApiResult,
} from '@/lib/api';
import { ruiLookupKey, type RuiLookupState } from './ruiLookupView';

/**
 * On-blur RUI cache lookup for the wizard (PR-ONBOARDING-RUI-AUTO-LOOKUP-1).
 * Stateless server call; the BE re-verifies + sources the date at submit, so
 * this is UX-only (badge + auto-filled read-only date). `unavailable` = network
 * / cache down → degrade silently.
 *
 * STALE-FIX: every successful run records the input `key` (`ruiLookupKey`) so a
 * result is only treated as valid for the tuple it was computed from. Pure view
 * helpers (tone/message/key/staleness) live in `ruiLookupView` and are
 * re-exported here for call-site convenience.
 */
export {
  ruiLookupKey,
  isRuiLookupStale,
  ruiLookupTone,
  ruiLookupMessage,
  formatDateIT,
  type RuiLookupState,
  type RuiLookupKeyInput,
} from './ruiLookupView';

export function useRuiLookup(): {
  state: RuiLookupState;
  run: (input: RuiLookupApiInput) => Promise<RuiLookupApiResult | null>;
  reset: () => void;
} {
  const [state, setState] = useState<RuiLookupState>({ status: 'idle' });

  const run = useCallback(
    async (input: RuiLookupApiInput): Promise<RuiLookupApiResult | null> => {
      if (!input.ruiNumber || input.ruiNumber.trim() === '') {
        setState({ status: 'idle' });
        return null;
      }
      setState({ status: 'loading' });
      const result = await lookupRui(input);
      if (!result) {
        setState({ status: 'unavailable' });
        return null;
      }
      setState({ status: 'done', result, key: ruiLookupKey(input) });
      return result;
    },
    [],
  );

  const reset = useCallback(() => setState({ status: 'idle' }), []);

  return { state, run, reset };
}
